import {useEffect, useRef, useState, type ReactElement} from 'react'
import {ArrowUpRightIcon} from '@primer/octicons-react'
import type {ChatState} from '@microsoft/agent-host-protocol'
import {announce} from '@github-ui/aria-live'

import type {Artifact} from '../../utils/artifacts/artifact-core'
import {ARTIFACTS_COLLAPSE_LABEL, ARTIFACTS_EXPAND_LABEL, ARTIFACTS_LABEL} from './artifacts-labels'
import {useArtifactWorkbench} from './use-artifact-workbench'
import {ActionBarTrigger} from '../task-view/AhpTaskChatInputActionBar/ActionBarTrigger'
import {AhpTaskChatInputActionBar} from '../task-view/AhpTaskChatInputActionBar/AhpTaskChatInputActionBar'
import {ArtifactItem} from '../task-view/AhpTaskChatInputActionBar/ArtifactItem'
import {ItemsPanel} from '../task-view/AhpTaskChatInputActionBar/ItemsPanel'
import {useSessionArtifacts, type PersistedArtifactHistory} from '../../hooks/use-session-artifacts'
import styles from './SessionArtifacts.module.css'

interface ArtifactsProps {
  artifacts: Artifact[]
  historyIsSettled?: boolean
  artifactWritesSettled?: boolean
  /**
   * Resolves the element to focus when every artifact row disappears at once and
   * the panel unmounts. Called at focus time, because the control that replaces
   * the panel can mount in the same commit that removes it.
   */
  getFallbackFocusTarget?: () => HTMLElement | null
}

export interface ArtifactsAction {
  panel: ReactElement | null
  trigger: ReactElement | null
  /** Attach to the surface that renders {@link ArtifactsAction.panel} so focus repair can track the focused row. */
  onSurfaceFocus: (event: React.FocusEvent<HTMLDivElement>) => void
  /** True only while the artifacts panel is expanded — unlike `panel`, which is non-null whenever
   * any artifacts exist at all. Consumers must use this flag, not `Boolean(panel)`. */
  isOpen: boolean
}

// Announces newly written artifacts once the session's own list grows, keyed on
// the session and chat state so a fresh mount or a session switch never fires.
function useAnnounceNewArtifacts(artifacts: Artifact[], sessionId: string | undefined, chatState?: ChatState) {
  const previousArtifactSnapshotRef = useRef<
    | {
        sessionId: string | undefined
        chatState: ChatState
        paths: Set<string>
      }
    | undefined
  >(undefined)

  useEffect(() => {
    if (!chatState) return

    const paths = new Set(artifacts.map(artifact => artifact.absolutePath))
    const previousSnapshot = previousArtifactSnapshotRef.current
    previousArtifactSnapshotRef.current = {sessionId, chatState, paths}
    if (
      previousSnapshot === undefined ||
      previousSnapshot.sessionId !== sessionId ||
      previousSnapshot.chatState === chatState
    ) {
      return
    }

    const addedCount = artifacts.filter(artifact => !previousSnapshot.paths.has(artifact.absolutePath)).length
    if (addedCount === 0) return

    announce(`${addedCount} new ${addedCount === 1 ? 'artifact' : 'artifacts'} available.`)
  }, [artifacts, chatState, sessionId])
}

export function useAnnouncedSessionArtifacts(chatState?: ChatState, persistedHistory?: PersistedArtifactHistory) {
  const {artifacts, sessionId, historyIsSettled, artifactWritesSettled} = useSessionArtifacts(
    chatState,
    persistedHistory,
  )
  useAnnounceNewArtifacts(artifacts, sessionId, chatState)

  return {artifacts, historyIsSettled, artifactWritesSettled}
}

/**
 * The artifacts control for an agent session, wired to the surrounding session
 * context. The transcript-specific data shaping stays in useSessionArtifacts;
 * the rendered controls reuse the AHP action bar primitives.
 */
export function SessionArtifacts({
  chatState,
  getFallbackFocusTarget,
}: {
  chatState?: ChatState
  getFallbackFocusTarget?: () => HTMLElement | null
}) {
  const {artifacts, sessionId, historyIsSettled, artifactWritesSettled} = useSessionArtifacts(chatState)
  useAnnounceNewArtifacts(artifacts, sessionId, chatState)

  return (
    <Artifacts
      artifacts={artifacts}
      historyIsSettled={historyIsSettled}
      artifactWritesSettled={artifactWritesSettled}
      getFallbackFocusTarget={getFallbackFocusTarget}
    />
  )
}

export function useArtifactsAction(
  sourceArtifacts: Artifact[],
  getFallbackFocusTarget?: () => HTMLElement | null,
  historyIsSettled = true,
  artifactWritesSettled = false,
): ArtifactsAction {
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedPath, setSelectedPath] = useState<string | undefined>(undefined)
  // The path last selected for its inline preview. That preview stays until
  // another selection, even if the row becomes openable after a reconnect.
  const [inlinePreviewPath, setInlinePreviewPath] = useState<string | undefined>(undefined)
  const artifactWorkbench = useArtifactWorkbench(sourceArtifacts, historyIsSettled, artifactWritesSettled)
  const artifacts = artifactWorkbench.artifacts
  const openInWorkbench = artifactWorkbench.openArtifact
  const triggerRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const wasExpandedRef = useRef(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const focusedArtifactPathRef = useRef<string | undefined>(undefined)
  const focusWasInSurfaceRef = useRef(false)
  const previousPathsRef = useRef<string[]>([])

  const canOpenInWorkbench = (artifact: Artifact) => artifactWorkbench.canOpenArtifact(artifact)

  const selected = artifacts.find(artifact => artifact.absolutePath === selectedPath)

  // The design shows bare file names, which is ambiguous when the agent wrote
  // two files of the same name in different folders. Only those rows fall back
  // to showing their directory, so the common case stays uncluttered.
  const names = artifacts.map(artifact => artifact.fileName)
  const ambiguousNames = new Set(names.filter((name, index) => names.indexOf(name) !== index))

  useEffect(() => {
    if (isExpanded) {
      closeButtonRef.current?.focus()
      wasExpandedRef.current = true
    } else if (wasExpandedRef.current) {
      triggerRef.current?.focus()
      wasExpandedRef.current = false
    }
  }, [isExpanded])

  // Live rename reconciliation can unmount the artifact row the user is on:
  // rows are keyed by absolutePath, so a move swaps the row for a new key and
  // the browser drops keyboard focus to <body>. The preview-side sync only
  // repairs focus inside the workbench pane, so repair it here for the list.
  // Restore focus to the row now at that position (the renamed row, since a move
  // keeps list order) or the nearest survivor, falling back to the close button.
  useEffect(() => {
    const currentPaths = artifacts.map(artifact => artifact.absolutePath)
    const previousPaths = previousPathsRef.current
    previousPathsRef.current = currentPaths

    // The whole surface unmounts when the last artifact goes, taking the trigger
    // and close button with it, so any focus inside it needs a target that outlives it.
    if (currentPaths.length === 0) {
      if (previousPaths.length > 0 && focusWasInSurfaceRef.current) {
        focusWasInSurfaceRef.current = false
        if (document.activeElement === null || document.activeElement === document.body) {
          getFallbackFocusTarget?.()?.focus()
        }
      }
      return
    }

    const focusedPath = focusedArtifactPathRef.current
    if (focusedPath === undefined || currentPaths.includes(focusedPath) || !previousPaths.includes(focusedPath)) {
      return
    }
    // Only repair focus actually lost to the unmount, never a deliberate move.
    const active = document.activeElement
    if (active !== null && active !== document.body) return

    const panel = panelRef.current
    const rows = panel?.querySelectorAll<HTMLElement>('[data-artifact-path]')
    const removedIndex = previousPaths.indexOf(focusedPath)
    // Every artifact can disappear at once, unmounting the panel and its close
    // button, so fall back to a target the parent guarantees outlives them.
    const target =
      rows?.[Math.min(removedIndex, rows.length - 1)] ?? closeButtonRef.current ?? getFallbackFocusTarget?.()
    target?.focus()
  }, [artifacts, getFallbackFocusTarget])

  const handleSurfaceFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    const row = (event.target as HTMLElement).closest<HTMLElement>('[data-artifact-path]')
    focusedArtifactPathRef.current = row?.getAttribute('data-artifact-path') ?? undefined
    // The trigger and close button live in this surface too, and they disappear
    // with it, so remember focus was here even when it is not on a row.
    focusWasInSurfaceRef.current = true
  }

  const panel =
    artifacts.length > 0 ? (
      <>
        {isExpanded && (
          <div data-testid="session-artifacts-panel" ref={panelRef}>
            <ItemsPanel
              title={ARTIFACTS_LABEL}
              closeButtonRef={closeButtonRef}
              onClose={() => setIsExpanded(false)}
              items={artifacts.map(artifact => (
                <ArtifactItem
                  key={artifact.absolutePath}
                  label={artifact.fileName}
                  data-artifact-path={artifact.absolutePath}
                  description={
                    ambiguousNames.has(artifact.fileName) && artifact.directory ? artifact.directory : undefined
                  }
                  trailingVisual={canOpenInWorkbench(artifact) ? ArrowUpRightIcon : undefined}
                  active={artifact.absolutePath === selectedPath}
                  onSelect={event => {
                    if (canOpenInWorkbench(artifact)) {
                      void openInWorkbench?.(artifact, event as React.MouseEvent)
                      setSelectedPath(artifact.absolutePath)
                      setInlinePreviewPath(undefined)
                      return
                    }
                    artifactWorkbench.cancelPendingOpen()
                    const nextPath = selectedPath === artifact.absolutePath ? undefined : artifact.absolutePath
                    setSelectedPath(nextPath)
                    setInlinePreviewPath(nextPath)
                  }}
                />
              ))}
            />
          </div>
        )}
        {isExpanded && selected && (!canOpenInWorkbench(selected) || selected.absolutePath === inlinePreviewPath) && (
          <div className={styles.preview} data-testid="session-artifacts-preview">
            <div className={styles.previewHeader}>{selected.displayPath}</div>
            {selected.preview ? (
              <pre className={styles.previewBody}>{selected.preview}</pre>
            ) : (
              <p className={styles.previewEmpty}>No preview available for this file.</p>
            )}
          </div>
        )}
      </>
    ) : null

  const trigger =
    artifacts.length > 0 ? (
      <ActionBarTrigger
        ref={triggerRef}
        count={artifacts.length}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-label={isExpanded ? ARTIFACTS_COLLAPSE_LABEL : ARTIFACTS_EXPAND_LABEL}
      >
        {ARTIFACTS_LABEL}
      </ActionBarTrigger>
    ) : null

  return {panel, trigger, onSurfaceFocus: handleSurfaceFocus, isOpen: artifacts.length > 0 && isExpanded}
}

export function Artifacts({
  artifacts,
  historyIsSettled = true,
  artifactWritesSettled = false,
  getFallbackFocusTarget,
}: ArtifactsProps) {
  const action = useArtifactsAction(artifacts, getFallbackFocusTarget, historyIsSettled, artifactWritesSettled)

  // Mirrors the changed-files header, which hides itself when there is nothing
  // to show rather than rendering an empty affordance above the composer.
  if (artifacts.length === 0) return null

  return (
    <div className={styles.root} data-testid="session-artifacts" onFocus={action.onSurfaceFocus}>
      {action.panel}
      <AhpTaskChatInputActionBar>{action.trigger}</AhpTaskChatInputActionBar>
    </div>
  )
}
